import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Marktanalysen";
const h1Text = "Fundierte Marktanalysen für Ihren erfolgreichen DACH-Markteintritt. Als Ihre operative Schnittstelle beschaffen wir Ihnen präzise Business Intelligence und Marktforschungsleistungen, um Marktbedürfnisse, Marktgrößen und die etablierte Konkurrenz exakt zu verstehen.";
const heroImage = "https://net-basis.net/Bilder/market-analysis-business-intelligence-dach-market-entry.webp";
const heroAlt = "Geschäftsmann analysiert digitale Marktdaten für fundierte Entscheidungen beim Markteintritt.";
const heroClaimLine1 = "Märkte verstehen!";
const heroClaimLine2 = "Sicher entscheiden!";
const benefitsTitle = "Wie Sie von unserer Expertise im Bereich __Marktanalysen__ profitieren:";
const p1Text = "NetBasis International bietet seinen Kunden als wichtige Komponente ihrer Geschäftsstrategie und zugleich entscheidenden Schlüsselfaktor im Wettbewerb ein umfangreiches Paket an Marktforschungsleistungen an. Wir liefern ausländischen ITK-Herstellern alle relevanten Informationen, die notwendig sind, um sachkundige und sichere Geschäftsentscheidungen vor Ort zu treffen.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Analyse von Markttrends, Marktaktivitäten und Branchen-Themen",
  "Beurteilung von Marktgrößen, spezifischen Marktmerkmalen und Trends",
  "Hintergrundberichte zu ausländischen Wettbewerbern und ihrer lokalen Aufstellung, ihren Auftraggebern und Partnern oder Kunden",
  "Strategische Festlegung von passgenauen Marketing- und Managementstrategien",
  "Präzise Marktdefinitionen aufgeteilt nach potenziellen Partner- und Kundengruppen",
  "Definition von Lieferanten, Kunden und Partnern, Händlern oder Distributoren",
  "Prüfung und Klärung aller behördlichen Prozeduren und lokalen Richtlinien",
];

export default function MarktanalysenDePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Marktanalysen | NetBasis International"
        description="Fundierte Daten und präzise Einblicke in die DACH-Region als solide Entscheidungsbasis für Ihre strategische Expansion."
        ogUrl="https://net-basis.net/de/services/business-development/marktanalysen"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-end pt-[6%] md:pt-[4%] lg:pt-[3%] pr-[8%] md:pr-[4%] lg:pr-[4%] pointer-events-none">
                <p
                  className="text-[1.625rem] md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right marktanalysen-hero-text-slide ma-hero-lh-15"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  <span className="block">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}